import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Database, FileSpreadsheet, FileText, Loader2, Trophy, CalendarClock, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { exportExcel, exportPdfTable, fmtDate, fmtPhone } from '@/lib/exporters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
  'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const AdminContactsTab = () => {
  const [loading, setLoading] = useState(true);
  const [grouped, setGrouped] = useState(true);
  const [data, setData] = useState({ stats: { total: 0, in_period: 0, last_30_days: 0, top_companies: [] }, items: [], companies: [], cities: [] });
  const [f, setF] = useState({ company_id: '', uf: '', city: '', birthday_month: '', date_from: '', date_to: '', ddd: '', rating_type: '', order: 'recent' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
      const res = await axios.get(`${API}/admin/contacts`, {
        params, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setData(res.data);
    } catch { toast.error('Erro ao carregar banco de dados'); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  // Um mesmo WhatsApp pode avaliar em varias empresas: agrupamos mostrando todas as empresas vinculadas.
  const view = useMemo(() => {
    if (!grouped) return data.items.map(c => ({ ...c, companies: [c.company_name], places: [c.company_city ? `${c.company_city}/${c.company_state}` : ''] }));
    const map = new Map();
    data.items.forEach(c => {
      const key = String(c.whatsapp || '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '') || c.email || c.id;
      const cur = map.get(key);
      if (!cur) {
        map.set(key, { ...c, companies: [c.company_name], places: [c.company_city ? `${c.company_city}/${c.company_state}` : ''] });
        return;
      }
      if (!cur.companies.includes(c.company_name)) cur.companies.push(c.company_name);
      const place = c.company_city ? `${c.company_city}/${c.company_state}` : '';
      if (place && !cur.places.includes(place)) cur.places.push(place);
      if (!cur.birth_date && c.birth_date) cur.birth_date = c.birth_date;
      if ((c.created_at || '') > (cur.created_at || '')) cur.created_at = c.created_at;
    });
    return Array.from(map.values());
  }, [data.items, grouped]);

  const rows = view.map(c => ({
    Nome: c.name,
    WhatsApp: fmtPhone(c.whatsapp),
    'E-mail': c.email,
    Nascimento: fmtDate(c.birth_date),
    'Empresa(s)': c.companies.filter(Boolean).join(' · ') || '—',
    'Cidade/UF': c.places.filter(Boolean).join(' · ') || '—',
    Cadastro: fmtDate(c.created_at),
  }));

  const stamp = new Date().toISOString().slice(0, 10);
  const handleExcel = () => rows.length
    ? exportExcel(rows, `base-geral-usuarios-${stamp}`, 'Usuários')
    : toast.error('Nada para exportar');
  const handlePdf = () => rows.length
    ? exportPdfTable('Base Geral de Usuários', Object.keys(rows[0]), rows.map(r => Object.values(r)),
      `base-geral-usuarios-${stamp}`, `${rows.length} ${grouped ? 'pessoa(s) · duplicatas agrupadas pelo WhatsApp' : 'registro(s)'}`)
    : toast.error('Nada para exportar');

  const ufs = [...new Set(data.cities.map(c => c.split('/')[1]))].sort();
  const citiesOfUf = data.cities.filter(c => !f.uf || c.endsWith(`/${f.uf}`)).map(c => c.split('/')[0]);

  return (
    <div className="space-y-4" data-testid="admin-contacts-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-total-contacts">
          <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-bold mb-1">
            <Database className="w-3.5 h-3.5" /> Total de cadastros
          </div>
          <p className="text-2xl font-bold text-[#0A2647]">{data.stats.total}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-last-30-days">
          <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-bold mb-1">
            <CalendarClock className="w-3.5 h-3.5" /> Últimos 30 dias
          </div>
          <p className="text-2xl font-bold text-[#C5A059]">{data.stats.last_30_days || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-period-contacts">
          <p className="text-slate-400 text-xs uppercase tracking-wider font-bold mb-1">No filtro atual</p>
          <p className="text-2xl font-bold text-emerald-600">{data.stats.in_period}</p>
          {grouped && <p className="text-[11px] text-slate-400">{view.length} pessoa(s) após agrupar</p>}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-top-companies">
          <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-bold mb-1">
            <Trophy className="w-3.5 h-3.5" /> Top 5 empresas
          </div>
          <ol className="text-xs text-slate-600 space-y-0.5">
            {data.stats.top_companies.length
              ? data.stats.top_companies.map((t, i) => (
                <li key={i}><span className="font-semibold">{i + 1}.</span> {t.name} — {t.total}</li>
              ))
              : <li className="text-slate-400">Sem cadastros ainda</li>}
          </ol>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3">
          <select value={f.company_id} onChange={e => setF({ ...f, company_id: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-company">
            <option value="">Todas as empresas</option>
            {data.companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={f.uf} onChange={e => setF({ ...f, uf: e.target.value, city: '' })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-uf">
            <option value="">Todos os estados</option>
            {ufs.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          <select value={f.city} onChange={e => setF({ ...f, city: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-city">
            <option value="">Todas as cidades</option>
            {citiesOfUf.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={f.birthday_month} onChange={e => setF({ ...f, birthday_month: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-birthday">
            <option value="">Aniversariantes: todos</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="date" value={f.date_from} onChange={e => setF({ ...f, date_from: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-from" />
          <input type="date" value={f.date_to} onChange={e => setF({ ...f, date_to: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-to" />
          <input placeholder="DDD" maxLength={2} value={f.ddd}
            onChange={e => setF({ ...f, ddd: e.target.value.replace(/\D/g, '') })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-ddd" />
          <select value={f.order} onChange={e => setF({ ...f, order: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-order">
            <option value="recent">Mais recentes</option>
            <option value="az">Nome A-Z</option>
            <option value="za">Nome Z-A</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={f.rating_type} onChange={e => setF({ ...f, rating_type: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-rating">
            <option value="">Avaliação: todas</option>
            <option value="positive">Positivas</option>
            <option value="negative">Negativas</option>
          </select>
          <button type="button" onClick={() => setGrouped(g => !g)} data-testid="toggle-group-duplicates"
            className={`inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border transition-colors ${grouped ? 'bg-[#0A2647] text-white border-[#0A2647]' : 'bg-white text-slate-600 border-slate-200'}`}>
            <Layers className="w-4 h-4" /> {grouped ? 'Duplicatas agrupadas' : 'Mostrando todos os registros'}
          </button>
          <Button onClick={handleExcel} size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="export-excel-btn">
            <FileSpreadsheet className="w-4 h-4" /> Exportar Excel
          </Button>
          <Button onClick={handlePdf} size="sm" className="bg-[#0A2647] hover:bg-[#0e3260] gap-2" data-testid="export-pdf-btn">
            <FileText className="w-4 h-4" /> Exportar PDF
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        ) : !view.length ? (
          <p className="py-16 text-center text-slate-500 text-sm" data-testid="contacts-empty">
            Nenhum cadastro encontrado com esses filtros.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-contacts-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-3">Nome</th>
                  <th className="text-left px-3 py-3">WhatsApp</th>
                  <th className="text-left px-3 py-3">E-mail</th>
                  <th className="text-left px-3 py-3">Nascimento</th>
                  <th className="text-left px-3 py-3">Empresa(s)</th>
                  <th className="text-left px-3 py-3">Cidade</th>
                  <th className="text-left px-3 py-3">Cadastro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {view.map(c => (
                  <tr key={c.id} className="hover:bg-slate-50" data-testid={`admin-contact-row-${c.id}`}>
                    <td className="px-3 py-2.5 font-medium text-slate-800">{c.name}</td>
                    <td className="px-3 py-2.5">{fmtPhone(c.whatsapp)}</td>
                    <td className="px-3 py-2.5 text-slate-600">{c.email}</td>
                    <td className="px-3 py-2.5 text-slate-600">{fmtDate(c.birth_date)}</td>
                    <td className="px-3 py-2.5 text-slate-700">
                      {c.companies.filter(Boolean).join(' · ') || '—'}
                      {c.companies.length > 1 && (
                        <span className="ml-1 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                          {c.companies.length} empresas
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-slate-500">{c.places.filter(Boolean).join(' · ') || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-500">{fmtDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContactsTab;
